import React, { useEffect, useState } from "react";
import { listUsers, createUser, deleteUser } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, Shield, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const EMPTY = { email: "", password: "", name: "", role: "user" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setUsers(await listUsers());
    } catch {
      toast.error("Kullanıcılar yüklenemedi");
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("E-posta ve şifre zorunlu");
    setBusy(true);
    try {
      await createUser({ ...form, email: form.email.trim().toLowerCase() });
      toast.success("Kullanıcı eklendi");
      setForm(EMPTY);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Oluşturulamadı";
      toast.error(typeof msg === "string" ? msg : "Oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Kullanıcıyı ve tüm kayıtlarını silmek istediğinize emin misiniz?")) return;
    try {
      await deleteUser(id);
      toast.success("Silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-xl bg-[#2F5D3D]/10 flex items-center justify-center text-[#2F5D3D]">
          <Users className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Kullanıcı Yönetimi</h1>
          <p className="text-sm text-muted-foreground">Yeni kullanıcı ekleyin ve mevcut kullanıcıları yönetin.</p>
        </div>
      </div>

      {/* Create form */}
      <div className="bg-white rounded-2xl border border-[#E8E2D0] p-6 shadow-sm">
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <UserPlus className="size-4" /> Yeni Kullanıcı Ekle
        </h2>
        <form onSubmit={onCreate} className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <Label htmlFor="email">E-posta</Label>
            <Input id="email" type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="mt-1.5" data-testid="new-user-email" />
          </div>
          <div>
            <Label htmlFor="name">Ad Soyad</Label>
            <Input id="name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="mt-1.5" data-testid="new-user-name" />
          </div>
          <div>
            <Label htmlFor="password">Şifre</Label>
            <Input id="password" type="text" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   className="mt-1.5" data-testid="new-user-password" />
          </div>
          <div>
            <Label htmlFor="role">Rol</Label>
            <select id="role" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="new-user-role">
              <option value="user">Kullanıcı</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="md:col-span-5 flex justify-end">
            <Button type="submit" disabled={busy} className="rounded-full gap-2 bg-[#2F5D3D] hover:bg-[#264b31]"
                    data-testid="create-user-btn">
              <UserPlus className="size-4" /> {busy ? "Ekleniyor…" : "Ekle"}
            </Button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#E8E2D0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6EF] text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-5 py-3">E-posta</th>
              <th className="px-5 py-3">Ad</th>
              <th className="px-5 py-3">Rol</th>
              <th className="px-5 py-3 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#E8E2D0]" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.email}</td>
                <td className="px-5 py-3 text-muted-foreground">{u.name || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${u.role === "admin" ? "bg-[#2F5D3D] text-white" : "bg-muted text-foreground"}`}>
                    {u.role === "admin" ? <Shield className="size-3" /> : <UserIcon className="size-3" />}
                    {u.role === "admin" ? "Admin" : "Kullanıcı"}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  {u.id !== me?.id && (
                    <Button variant="ghost" size="sm" onClick={() => onDelete(u.id)}
                            className="text-destructive hover:text-destructive" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-muted-foreground">Henüz kullanıcı yok</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
